import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HelpCircle, Maximize2, Minimize2 } from 'lucide-react';
import { useT } from '../context/LanguageContext';

/* The two plain icon controls in the header, kept beside the bell and the
   theme switch so the right-hand cluster reads as one row of tools. */

const BTN = 'p-2 rounded-md text-muted-foreground hover:bg-accent '
  + 'hover:text-accent-foreground transition-colors';

/* The manual, one tap from anywhere. RestoBOXX-InTutor is a real screen, so
   this is a link to it rather than a tooltip pretending to be help. */
export function HeaderHelpButton() {
  const navigate = useNavigate();
  const { t } = useT();
  return (
    <button
      type="button"
      onClick={() => navigate('/help')}
      aria-label={t('header.help')}
      title={t('header.help')}
      className={BTN}
    >
      <HelpCircle size={16} />
    </button>
  );
}

/* Full screen — a till on a small counter buys back the browser's chrome.
   The button follows the DOCUMENT, not its own click: pressing Escape or F11
   leaves fullscreen without telling us, and a button that then still said
   "exit" would be lying, so `fullscreenchange` is what sets the state. */
export function FullscreenToggle() {
  const { t } = useT();
  const [full, setFull] = useState(
    () => (typeof document !== 'undefined' && !!document.fullscreenElement),
  );

  useEffect(() => {
    const sync = () => setFull(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);

  const toggle = useCallback(() => {
    /* Both calls reject when the browser refuses (an iframe without the
       permission, a user gesture it did not like). Swallowed: the state is
       driven by fullscreenchange, so a refusal simply leaves the button as
       it was rather than throwing into the console. */
    if (document.fullscreenElement) {
      document.exitFullscreen?.().catch(() => {});
    } else {
      document.documentElement.requestFullscreen?.().catch(() => {});
    }
  }, []);

  // A browser without the API at all gets no button rather than a dead one.
  if (typeof document !== 'undefined' && !document.documentElement.requestFullscreen) {
    return null;
  }

  const label = full ? t('header.exitFullscreen') : t('header.fullscreen');
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={label}
      title={label}
      aria-pressed={full}
      className={`hidden sm:inline-flex ${BTN}`}
    >
      {full ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
    </button>
  );
}
